import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit,
  Output, SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, switchMap, catchError, debounceTime, groupBy, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/core/app.state';
import { FetchAllCustomFields } from '../../store/settings.actions';
import { IApplicabilityField, ICustomField, ISplittingRule, ISplittingRuleCreate } from '../../models/settings.models';
import { FetchAllBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchAllCategories } from 'src/app/features/categories/store/categories.actions';
import { FetchAllTags } from 'src/app/features/tags/store/tags.actions';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { SettingsService } from '../../services/settings.services';

export interface CustomerChip { id: number; name: string; }

export interface ConditionItem {
  type: 'customer' | 'customer_group' | 'custom_field';
  chipValues: string[];       // customer_group: text values
  customerChips: CustomerChip[]; // customer: id+name pairs
  fieldId: number | null;
  fieldValue: string;
  _input: string;             // customer_group free-text
}

@Component({
  selector: 'app-splitting-rule-drawer',
  templateUrl: './splitting-rule-drawer.component.html',
  styleUrls: ['./splitting-rule-drawer.component.scss'],
})
export class SplittingRuleDrawerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Input() rule: ISplittingRule | null = null;
  @Input() existingRules: ISplittingRule[] = [];
  @Output() save = new EventEmitter<ISplittingRuleCreate>();
  @Output() closeDrawer = new EventEmitter<void>();

  form!: FormGroup;

  // Split values state
  splitValues: string[] = [];
  splitSearch = '';
  splitDropdownOpen = false;

  // Applicability state
  applicabilityMode: 'all' | 'conditions' = 'all';
  conditions: ConditionItem[] = [];
  conditionSearches: string[] = [];
  conditionDropdownOpen: boolean[] = [];
  conditionValueSearches: string[] = [];
  conditionValueDropdownOpen: boolean[] = [];

  // Reference data from store
  productCustomFields: ICustomField[] = [];
  brands: IBrand[] = [];
  categories: ICategory[] = [];
  tags: ITag[] = [];

  // Customer typeahead: live server-side search results per condition row
  // (keyed to res.partner/locations — what applicability_partner_ids actually expects).
  conditionCustomerResults: CustomerChip[][] = [];
  conditionCustomerLoading: boolean[] = [];
  private customerSearch$ = new Subject<{ i: number; q: string }>();

  // Customer custom fields for the "Apply to" section
  customerCustomFields: IApplicabilityField[] = [];
  customerCustomFieldsLoading = false;

  // Customer groups for the "Apply to → customer_group" typeahead
  customerGroupOptions: CustomerChip[] = [];
  customerGroupsLoading = false;

  // Dynamic custom field values (fetched when a field is selected)
  customFieldValues: string[] = [];
  customFieldValuesLoading = false;

  private destroy$ = new Subject<void>();

  get splitTypeOptions() {
    const t = (k: string) => this.translate.instant('settings.' + k);
    return [
      { value: 'brand',                label: t('splitting_rules_opt_brand'),        sub: t('splitting_rules_opt_brand_sub') },
      { value: 'category',             label: t('splitting_rules_opt_category'),     sub: t('splitting_rules_opt_category_sub') },
      { value: 'product_label',        label: t('splitting_rules_opt_label'),        sub: t('splitting_rules_opt_label_sub') },
      { value: 'product_custom_field', label: t('splitting_rules_opt_custom_field'), sub: t('splitting_rules_opt_custom_field_sub') },
    ];
  }

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private settingsService: SettingsService,
    private customersService: CustomersService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name:                      ['', Validators.required],
      condition_type:            ['brand'],
      condition_custom_field_id: [null],
      split_mode:                ['all'],
    });

    // Dispatch fetches (idempotent — effects skip if data already loaded)
    this.store.dispatch(FetchAllCustomFields());
    this.store.dispatch(FetchAllBrands({}));
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchAllTags({}));

    this.store.select(s => s.settings.customFields).pipe(takeUntil(this.destroy$))
      .subscribe(f => this.productCustomFields = (f || []).filter(x => x.object_type === 'inventory'));

    this.store.select(s => s.brands.allBrands).pipe(takeUntil(this.destroy$))
      .subscribe(b => this.brands = b || []);
    this.store.select(s => s.categories.allCategories).pipe(takeUntil(this.destroy$))
      .subscribe(c => this.categories = c || []);
    this.store.select(s => s.tags.allTags).pipe(takeUntil(this.destroy$))
      .subscribe(t => this.tags = t || []);

    // Customer typeahead: debounce + cancel-in-flight per row (grouped by
    // condition index), searched server-side against /locations (res.partner).
    // Empty query still searches -- shows a default page of up to 50 so the
    // dropdown isn't empty on first focus, before the user types anything.
    this.customerSearch$.pipe(
      groupBy(x => x.i),
      mergeMap(group$ => group$.pipe(
        debounceTime(300),
        switchMap(({ i, q }) => {
          this.conditionCustomerLoading[i] = true;
          return this.customersService.listLocations({ number: 1, size: 50 }, undefined, q).pipe(
            mergeMap(res => of({ i, res })),
            catchError(() => of({ i, res: null })),
          );
        }),
      )),
      takeUntil(this.destroy$),
    ).subscribe(result => {
      if (!result) return;
      const { i, res } = result;
      this.conditionCustomerLoading[i] = false;
      const selected = new Set((this.conditions[i]?.customerChips || []).map(x => x.id));
      this.conditionCustomerResults[i] = ((res?.data as any[]) || [])
        .filter((loc: any) => !selected.has(loc.id))
        .map((loc: any) => ({ id: loc.id, name: loc.name }));
    });

    this.customerCustomFieldsLoading = true;
    this.settingsService.listApplicabilityFields().pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.customerCustomFieldsLoading = false;
      this.customerCustomFields = res?.object?.data ?? [];
    });

    this.customerGroupsLoading = true;
    this.settingsService.listCustomerGroups().pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.customerGroupsLoading = false;
      this.customerGroupOptions = (res?.object?.data ?? []).map((c: any) => ({ id: c.id, name: c.name }));
    });

    this.form.get('condition_custom_field_id')!.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap(fieldId => {
        this.customFieldValues = [];
        if (!fieldId) return of(null);
        this.customFieldValuesLoading = true;
        return this.settingsService.listCustomFieldValues(fieldId).pipe(
          catchError(() => of(null)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.customFieldValuesLoading = false;
      this.customFieldValues = res?.object?.values ?? [];
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true && this.form) {
      this.rule ? this.patchForm(this.rule) : this.resetForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── When-to-split helpers ─────────────────────────────────────────────────

  get nameAlreadyExists(): boolean {
    const name = this.form?.get('name')?.value?.trim().toLowerCase();
    if (!name) return false;
    return this.existingRules.some(r =>
      r.name.toLowerCase() === name && r.id !== this.rule?.id
    );
  }

  get conditionType(): string { return this.form?.get('condition_type')?.value || 'brand'; }
  get splitMode(): string { return this.form?.get('split_mode')?.value || 'all'; }
  get canUseSpecificMode(): boolean { return this.conditionType !== 'product_label'; }
  get isTypeaheadSplit(): boolean {
    if (['brand', 'category', 'product_label'].includes(this.conditionType)) return true;
    if (this.conditionType === 'product_custom_field' && !!this.form.get('condition_custom_field_id')?.value) return true;
    return false;
  }

  selectConditionType(type: string): void {
    this.form.get('condition_type')?.setValue(type);
    if (type !== 'product_custom_field') {
      this.form.get('condition_custom_field_id')?.setValue(null);
      this.customFieldValues = [];
      this.customFieldValuesLoading = false;
    }
    if (type === 'product_label') this.form.get('split_mode')?.setValue('all');
    this.splitValues = [];
    this.splitSearch = '';
    this.splitDropdownOpen = false;
  }

  setSplitMode(mode: 'all' | 'specific'): void {
    if (mode === 'specific' && !this.canUseSpecificMode) return;
    this.form.get('split_mode')?.setValue(mode);
    if (mode === 'all') { this.splitValues = []; this.splitSearch = ''; }
  }

  // ── Typeahead split value helpers ────────────────────────────────────────

  get filteredSplitOptions(): Array<{ id: number; name: string }> {
    const q = this.splitSearch.toLowerCase().trim();
    if (this.conditionType === 'product_custom_field') {
      return this.customFieldValues
        .filter(v => !this.splitValues.includes(v) && v.toLowerCase().includes(q))
        .slice(0, 15)
        .map((v, i) => ({ id: i, name: v }));
    }
    let source: Array<{ id: number; name: string }> = [];
    if (this.conditionType === 'brand')              source = this.brands;
    else if (this.conditionType === 'category')       source = this.categories;
    else if (this.conditionType === 'product_label')  source = this.tags;
    return source
      .filter(x => !this.splitValues.includes(x.name) && x.name.toLowerCase().includes(q))
      .slice(0, 10);
  }

  addSplitItem(name: string): void {
    if (!this.splitValues.includes(name)) this.splitValues = [...this.splitValues, name];
    this.splitSearch = '';
    this.splitDropdownOpen = false;
  }

  removeSplitValue(v: string): void {
    this.splitValues = this.splitValues.filter(x => x !== v);
  }

  onSplitSearchFocus(): void { this.splitDropdownOpen = true; }
  onSplitSearchBlur(): void { setTimeout(() => { this.splitDropdownOpen = false; }, 150); }

  // ── Free-text split value helpers (custom_field) ──────────────────────────

  splitValueInput = '';

  commitSplitValueInput(): void {
    const v = this.splitValueInput.trim();
    if (v && !this.splitValues.includes(v)) this.splitValues = [...this.splitValues, v];
    this.splitValueInput = '';
  }

  addSplitValueOnEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') { event.preventDefault(); this.commitSplitValueInput(); }
  }

  get specificValuesPlaceholder(): string {
    return this.translate.instant('settings.splitting_rules_field_value_placeholder');
  }

  get splitTypeaheadPlaceholder(): string {
    const t = (k: string) => this.translate.instant('settings.' + k);
    if (this.conditionType === 'brand')              return t('splitting_rules_search_brands');
    if (this.conditionType === 'category')           return t('splitting_rules_search_categories');
    if (this.conditionType === 'product_label')      return t('splitting_rules_search_labels');
    if (this.conditionType === 'product_custom_field') return t('splitting_rules_search_values');
    return '';
  }

  // ── Live preview ───────────────────────────────────────────────────────────

  get previewText(): string {
    const t = (k: string, p?: object) => this.translate.instant('settings.' + k, p);
    const tlMap: Record<string, string> = {
      brand:                t('splitting_rules_tl_brand'),
      category:             t('splitting_rules_tl_category'),
      product_label:        t('splitting_rules_tl_label'),
      product_custom_field: t('splitting_rules_tl_custom_field'),
    };
    const tl = tlMap[this.conditionType] || this.conditionType;
    if (this.splitMode === 'specific' && this.splitValues.length > 0) {
      const shown = this.splitValues.slice(0, 3).map(v => `"${v}"`).join(', ');
      const rest  = this.splitValues.length > 3 ? t('splitting_rules_preview_more', { n: this.splitValues.length - 3 }) : '';
      return t('splitting_rules_preview_specific', { tl, shown, rest });
    }
    return t('splitting_rules_preview_all', { tl });
  }

  getCustomFieldLabel(f: ICustomField): string {
    return f.label?.['en'] || f.label?.['fr'] || f.technical_name;
  }

  getValueOptionsForField(fieldId: number | null): string[] {
    if (!fieldId) return [];
    return this.customerCustomFields.find(f => f.id === fieldId)?.value_options ?? [];
  }

  // ── Apply-to: condition helpers ───────────────────────────────────────────

  addCondition(): void {
    this.conditions = [...this.conditions,
      { type: 'customer', chipValues: [], customerChips: [], fieldId: null, fieldValue: '', _input: '' }];
    this.conditionSearches.push('');
    this.conditionDropdownOpen.push(false);
    this.conditionValueSearches.push('');
    this.conditionValueDropdownOpen.push(false);
    this.conditionCustomerResults.push([]);
    this.conditionCustomerLoading.push(false);
  }

  removeCondition(i: number): void {
    this.conditions = this.conditions.filter((_, idx) => idx !== i);
    this.conditionSearches.splice(i, 1);
    this.conditionDropdownOpen.splice(i, 1);
    this.conditionValueSearches.splice(i, 1);
    this.conditionValueDropdownOpen.splice(i, 1);
    this.conditionCustomerResults.splice(i, 1);
    this.conditionCustomerLoading.splice(i, 1);
  }

  setConditionType(i: number, type: ConditionItem['type']): void {
    this.conditions = this.conditions.map((item, idx) =>
      idx === i ? { ...item, type, chipValues: [], customerChips: [], fieldId: null, fieldValue: '', _input: '' } : item
    );
    this.conditionSearches[i] = '';
    this.conditionDropdownOpen[i] = false;
    this.conditionValueSearches[i] = '';
    this.conditionValueDropdownOpen[i] = false;
    this.conditionCustomerResults[i] = [];
  }

  // ── Customer typeahead ────────────────────────────────────────────────────
  // Live server-side search against res.partner locations (see customerSearch$
  // pipeline in ngOnInit) — applicability_partner_ids expects location ids,
  // not wl.company ids, so this must search locations, not companies.

  getFilteredCustomers(i: number): CustomerChip[] {
    return this.conditionCustomerResults[i] || [];
  }

  addCustomerChip(i: number, customer: CustomerChip): void {
    const c = this.conditions[i];
    if (!c.customerChips.find(x => x.id === customer.id)) {
      this.conditions = this.conditions.map((item, idx) =>
        idx === i ? { ...item, customerChips: [...item.customerChips, customer] } : item
      );
    }
    this.conditionSearches[i] = '';
    this.conditionDropdownOpen[i] = false;
    this.conditionCustomerResults[i] = [];
  }

  removeCustomerChip(i: number, id: number): void {
    this.conditions = this.conditions.map((item, idx) =>
      idx === i ? { ...item, customerChips: item.customerChips.filter(x => x.id !== id) } : item
    );
  }

  onConditionSearchFocus(i: number): void {
    this.conditionDropdownOpen[i] = true;
    if (this.conditions[i]?.type === 'customer' && !this.conditionCustomerResults[i]?.length) {
      this.customerSearch$.next({ i, q: (this.conditionSearches[i] || '').trim() });
    }
  }
  onConditionSearchBlur(i: number): void  { setTimeout(() => { this.conditionDropdownOpen[i] = false; }, 150); }

  setConditionSearch(i: number, value: string): void {
    this.conditionSearches[i] = value;
    this.conditionDropdownOpen[i] = true;
    if (this.conditions[i]?.type === 'customer') {
      this.customerSearch$.next({ i, q: value.trim() });
    }
  }

  // ── Customer-group typeahead ──────────────────────────────────────────────

  getFilteredCustomerGroups(i: number): CustomerChip[] {
    const q = (this.conditionSearches[i] || '').toLowerCase().trim();
    const selected = new Set((this.conditions[i]?.customerChips || []).map(x => x.id));
    return this.customerGroupOptions
      .filter(g => !selected.has(g.id) && g.name.toLowerCase().includes(q))
      .slice(0, 10);
  }

  addCustomerGroupChip(i: number, group: CustomerChip): void {
    const c = this.conditions[i];
    if (!c.customerChips.find(x => x.id === group.id)) {
      this.conditions = this.conditions.map((item, idx) =>
        idx === i ? { ...item, customerChips: [...item.customerChips, group] } : item
      );
    }
    this.conditionSearches[i] = '';
    this.conditionDropdownOpen[i] = false;
  }

  // ── Custom field condition ────────────────────────────────────────────────

  setConditionFieldId(i: number, fieldId: number): void {
    this.conditions = this.conditions.map((item, idx) =>
      idx === i ? { ...item, fieldId: +fieldId || null, fieldValue: '' } : item
    );
    this.conditionValueSearches[i] = '';
    this.conditionValueDropdownOpen[i] = false;
  }

  // ── Custom field value typeahead ──────────────────────────────────────────

  getFilteredConditionValueOptions(i: number): string[] {
    const c = this.conditions[i];
    if (!c) return [];
    const opts = this.getValueOptionsForField(c.fieldId);
    const q = (this.conditionValueSearches[i] || '').toLowerCase().trim();
    return opts.filter(v => v.toLowerCase().includes(q)).slice(0, 15);
  }

  onConditionValueFocus(i: number): void { this.conditionValueDropdownOpen[i] = true; }
  onConditionValueBlur(i: number): void  { setTimeout(() => { this.conditionValueDropdownOpen[i] = false; }, 150); }

  setConditionValueSearch(i: number, value: string): void {
    this.conditionValueSearches[i] = value;
    this.conditionValueDropdownOpen[i] = true;
    this.setConditionFieldValue(i, value);
  }

  selectConditionValueFromDropdown(i: number, value: string): void {
    this.setConditionFieldValue(i, value);
    this.conditionValueSearches[i] = value;
    this.conditionValueDropdownOpen[i] = false;
  }

  setConditionFieldValue(i: number, fieldValue: string): void {
    this.conditions = this.conditions.map((item, idx) =>
      idx === i ? { ...item, fieldValue } : item
    );
  }

  // ── Validation ────────────────────────────────────────────────────────────

  get isValid(): boolean {
    if (!this.form?.get('name')?.value?.trim()) return false;
    if (this.nameAlreadyExists) return false;
    if (this.conditionType === 'product_custom_field' && !this.form.get('condition_custom_field_id')?.value) return false;
    if (this.splitMode === 'specific' && this.splitValues.length === 0) return false;
    if (this.applicabilityMode === 'conditions') {
      if (this.conditions.length === 0) return false;
      for (const c of this.conditions) {
        if (c.type === 'customer' && c.customerChips.length === 0) return false;
        if (c.type === 'customer_group' && c.customerChips.length === 0) return false;
        if (c.type === 'custom_field' && (!c.fieldId || !c.fieldValue.trim())) return false;
      }
    }
    return true;
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  submit(): void {
    if (!this.isValid) return;
    const f = this.form.value;

    let applicability_type: ISplittingRuleCreate['applicability_type'] = 'all';
    let applicability_partner_ids: number[] = [];
    let applicability_location_group_ids: number[] = [];
    let applicability_custom_field_id: number | null = null;
    let applicability_custom_field_value: string | null = null;

    if (this.applicabilityMode === 'conditions' && this.conditions.length > 0) {
      const first = this.conditions[0];
      if (first.type === 'customer') {
        applicability_type = 'customer';
        applicability_partner_ids = first.customerChips.map(x => x.id);
      } else if (first.type === 'customer_group') {
        applicability_type = 'customer_group';
        applicability_location_group_ids = first.customerChips.map(x => x.id);
      } else if (first.type === 'custom_field') {
        applicability_type = 'customer_custom_field';
        applicability_custom_field_id = first.fieldId;
        applicability_custom_field_value = first.fieldValue.trim() || null;
      }
    }

    const dto: ISplittingRuleCreate = {
      name: f.name.trim(),
      active: true,
      sequence: this.rule?.sequence ?? 10,
      condition_type: f.condition_type as ISplittingRuleCreate['condition_type'],
      condition_custom_field_id: f.condition_type === 'product_custom_field' ? f.condition_custom_field_id : null,
      split_mode: f.split_mode as 'all' | 'specific',
      split_values: f.split_mode === 'specific' ? [...this.splitValues] : [],
      applicability_type,
      applicability_partner_ids,
      applicability_location_group_ids,
      applicability_custom_field_id,
      applicability_custom_field_value,
    };
    this.save.emit(dto);
  }

  // ── Form helpers ──────────────────────────────────────────────────────────

  private resetForm(): void {
    this.form.reset({ name: '', condition_type: 'brand', condition_custom_field_id: null, split_mode: 'all' });
    this.splitValues = [];
    this.splitSearch = '';
    this.splitValueInput = '';
    this.customFieldValues = [];
    this.customFieldValuesLoading = false;
    this.applicabilityMode = 'all';
    this.conditions = [];
    this.conditionSearches = [];
    this.conditionDropdownOpen = [];
    this.conditionValueSearches = [];
    this.conditionValueDropdownOpen = [];
    this.conditionCustomerResults = [];
    this.conditionCustomerLoading = [];
  }

  private patchForm(rule: ISplittingRule): void {
    this.form.patchValue({
      name: rule.name,
      condition_type: rule.condition_type,
      condition_custom_field_id: rule.condition_custom_field_id ?? null,
      split_mode: rule.split_mode || 'all',
    });
    this.splitValues = [...(rule.split_values || [])];
    this.splitSearch = '';
    this.splitValueInput = '';

    if (rule.applicability_type === 'all') {
      this.applicabilityMode = 'all';
      this.conditions = [];
    } else if (rule.applicability_type === 'customer') {
      this.applicabilityMode = 'conditions';
      const chips: CustomerChip[] = (rule.applicability_partner_ids || []).map(p => ({ id: p.id, name: p.name }));
      this.conditions = [{ type: 'customer', chipValues: [], customerChips: chips, fieldId: null, fieldValue: '', _input: '' }];
    } else if (rule.applicability_type === 'customer_group') {
      this.applicabilityMode = 'conditions';
      const chips: CustomerChip[] = (rule.applicability_location_group_ids || []).map(g => ({ id: g.id, name: g.name }));
      this.conditions = [{ type: 'customer_group', chipValues: [], customerChips: chips, fieldId: null, fieldValue: '', _input: '' }];
    } else if (rule.applicability_type === 'customer_custom_field') {
      this.applicabilityMode = 'conditions';
      this.conditions = [{ type: 'custom_field', chipValues: [], customerChips: [], fieldId: rule.applicability_custom_field_id || null, fieldValue: rule.applicability_custom_field_value || '', _input: '' }];
    }
    this.conditionSearches = this.conditions.map(() => '');
    this.conditionDropdownOpen = this.conditions.map(() => false);
    this.conditionValueSearches = this.conditions.map(c => c.type === 'custom_field' ? c.fieldValue : '');
    this.conditionValueDropdownOpen = this.conditions.map(() => false);
    this.conditionCustomerResults = this.conditions.map(() => []);
    this.conditionCustomerLoading = this.conditions.map(() => false);
  }
}
